import { useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { toast } from "sonner";
import { Upload, FileAudio, FileText, File as FileIcon, ExternalLink } from "lucide-react";

const fmtSize = (n) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const Icon = ({ ct }) => ct?.startsWith("audio/") ? <FileAudio size={14} /> : ct === "application/pdf" ? <FileText size={14} /> : <FileIcon size={14} />;

export const RecordsAttachmentRow = ({ a, href }) => (
  <a data-testid={`records-attachment-${a.aid}`} href={href} target="_blank" rel="noreferrer"
    className="flex items-center gap-2 text-sm px-2.5 py-1.5 rounded-sm border hover:bg-[#F1EFE8]" style={{ borderColor: P.line, color: P.ink, background: "#fff" }}>
    <span style={{ color: P.navy }}><Icon ct={a.content_type} /></span>
    <span className="font-mono text-xs font-bold">{a.aid}</span>
    <span className="truncate flex-1">{a.filename}</span>
    <span className="text-xs" style={{ color: P.dim }}>{fmtSize(a.size || 0)}</span>
    <ExternalLink size={12} style={{ color: P.dim }} />
  </a>
);

export const RecordsAttachUpload = ({ no, code, onDone, compact = false }) => {
  const [files, setFiles] = useState([]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!files.length) { toast.error("Choose at least one file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("code", code); fd.append("description", desc);
      files.forEach((f) => fd.append("files", f));
      const r = await pub.post(`/records-requests/${encodeURIComponent(no)}/attachments`, fd);
      toast.success(`${r.data.attachments.length} file(s) attached · expires ${new Date(r.data.expires_at).toLocaleDateString()}`);
      setFiles([]); setDesc(""); onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed — please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="records-attach-upload" className={compact ? "space-y-2 pt-2" : "space-y-2"}>
      <div className="text-xs" style={{ color: P.dim }}>Audio (mp3/wav/m4a), PDF, Word (doc/docx) or txt · max 50MB each · 25 per upload · up to 100 per request · auto-deleted after 14 days</div>
      <input data-testid="records-attach-input" type="file" multiple accept=".mp3,.wav,.m4a,.pdf,.doc,.docx,.txt"
        onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))} className="w-full text-sm py-1" />
      {files.length > 0 && <div className="text-xs" style={{ color: P.ink }}>{files.map((f) => f.name).join(" · ")}</div>}
      <div className="flex gap-2">
        <input data-testid="records-attach-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What are these files? (optional)"
          className="flex-1 px-3 py-2 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8]" style={{ borderColor: P.line }} />
        <button data-testid="records-attach-submit" disabled={busy || !files.length} onClick={submit}
          className="px-4 py-2 text-sm font-bold text-white rounded-sm flex items-center gap-2 disabled:opacity-50 shrink-0" style={{ background: P.navy }}>
          <Upload size={14} /> {busy ? "UPLOADING…" : "ATTACH FILES"}
        </button>
      </div>
    </div>
  );
};
